/**
 * useDictionary — F12.N T5.4, rozszerzone w Etapie 3b o kontrakt SCALONY (lokalny+globalny).
 *
 * Pobiera szczegoly slownika (z items) po kodzie. Uzywane przez dynamic rendery
 * pol typu `select` z `dictionaryCode` (np. `kind` w helper-encji `addresses`
 * uzywa slownika `address_kinds`) ORAZ przez `DictionariesTab` (Settings → Słowniki).
 *
 * Endpoint: GET /api/dictionaries/:code → { dictionary: MergedDictionaryDetail }.
 * Kontrakt v2 (`apps/api/lib/dictionaries/merge.ts`): słownik może pochodzić z
 * lokalnej tabeli tenanta (`origin: 'local'`) albo z globalnego katalogu ITE
 * (`origin: 'global'`, `readonly: true`) — patrz `docs` scratchpad brief §3/§4.
 *
 * Items zawieraja zarowno aktywne jak i nieaktywne pozycje (backend nie filtruje).
 * Konsument decyduje co pokazuje:
 *  - tabela / detail readonly: mapuj kazdy item kod→label (fallback raw value gdy brak)
 *  - select w formularzu: filtruj `selectable=true` (aktywna I nie ukryta tenantowo)
 *
 * Stale time: 2 min (analogicznie do useSystemModuleActiveLayout).
 */

import type { DocumentClass } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

// ============================================================================
// Typy — kontrakt scalony (merge.ts: MergedDictionarySummary / MergedDictionaryItem)
// ============================================================================

export type DictionaryOrigin = 'local' | 'global' | 'extension' | 'external';
export type DictionaryStatus = 'published' | 'withdrawn' | null;

/**
 * Rodzaj pozycji słownika-rodzica (nie pojedynczej pozycji) — Etap 10 (ADR-0066). 'manual' —
 * pozycje wpisane ręcznie. 'external' — pozycje WYLICZANE z zapytania (nieedytowalne ręcznie),
 * dwa źródła na drucie mapowane na tę samą wartość: słownik GLOBALNY z rejestru źródeł ITE ORAZ
 * lokalny słownik tenanta z własnym SQL (DB `source_kind='sql'` → wire `'external'`). Renderery
 * i edytor podpięć rozstrzygają po `params`, nie po tym polu, więc oba przypadki działają tak
 * samo. Mirror `MergedDictionarySummary['sourceKind']` w `apps/api/lib/dictionaries/merge.ts`.
 */
export type DictionarySourceKind = 'manual' | 'external';

/**
 * Typ zadeklarowanego parametru źródła zewnętrznego (etap 1 modelu dwuetapowego, Faza 2b
 * ADR-0068) — mirror `DictionaryParamType` w `apps/api/lib/dictionaries/external-source-types.ts`.
 * Deklaracja NIE niesie już `kind` — źródło wartości wybiera się dopiero przy podpinaniu
 * pola (etap 2, `DictionaryParamBinding` niżej).
 */
export type DictionaryParamType = 'text' | 'int' | 'decimal' | 'date' | 'bool';

/**
 * Wszystkie typy parametru w kolejności prezentacji w edytorze deklaracji SQL (mirror
 * `DICTIONARY_SOURCE_PARAM_TYPES` po stronie globalnej) — zasila dropdown typu parametru w
 * `DictionaryFormModal` przy autorstwie lokalnego słownika SQL.
 */
export const DICTIONARY_PARAM_TYPES: readonly DictionaryParamType[] = [
  'text',
  'int',
  'decimal',
  'date',
  'bool',
] as const;

/**
 * Parametr zadeklarowany przez źródło zewnętrzne — kształt publiczny wysyłany przez backend
 * (mirror JSON-serializacji `DictionarySourceParam`). Pusta tablica dla `sourceKind==='manual'`.
 */
export interface DictionaryParam {
  name: string;
  type: DictionaryParamType;
  required: boolean;
}

/**
 * Wiązanie parametru słownika (etap 2, Faza 2b ADR-0068) — mirror `DictionaryParamBinding`
 * w `apps/api/lib/dictionaries/external-source-types.ts`. Ustalane na DEFINICJI POLA
 * (`dictionaryParamBindings`): skąd wziąć wartość zadeklarowanego parametru przy otwarciu
 * dropdownu. `context.company`/`context.user` rozwiązuje WYŁĄCZNIE serwer (nagłówek firmy /
 * JWT); `record_field` czyta wartość pola-sąsiada TEGO SAMEGO rekordu; `constant` niesie
 * literał ustawiony przy konfiguracji pola; `parent_field` czyta pole rekordu-RODZICA tabeli
 * dodatkowej — slug wybierany per klasa dokumentu albo `'default'` (`ParentFieldBindingKey`).
 */
export type DictionaryParamBinding =
  | { kind: 'record_field'; fieldSlug: string }
  | { kind: 'context.company' }
  | { kind: 'context.user' }
  | { kind: 'constant'; value: string | number | boolean }
  | { kind: 'parent_field'; fieldSlugs: Partial<Record<ParentFieldBindingKey, string>> };

/**
 * Klucz wyboru pola rodzica w wiązaniu `parent_field` — mirror `ParentFieldBindingKey`
 * z `apps/api/lib/dictionaries/external-source-types.ts`: klasa dokumentu (właściciel =
 * dokument, 7 klas dzieli jedną tabelę-właściciela) albo `'default'` (każdy inny właściciel).
 */
export type ParentFieldBindingKey = 'default' | DocumentClass;

/**
 * Wartość wpisu w mapie `dictionaryParamBindings` — tagowane wiązanie (nowe zapisy) ALBO
 * goły string = legacy `record_field` ze slugiem pola-sąsiada (stare wiersze jsonb sprzed
 * Fazy 2b; backend czyta oba kształty, NOWE zapisy muszą być tagowane).
 */
export type DictionaryParamBindingValue = string | DictionaryParamBinding;

/** Mapa NAZWA PARAMETRU źródła → wiązanie (kształt kolumny `dictionary_param_bindings`). */
export type DictionaryParamBindingsMap = Record<string, DictionaryParamBindingValue>;

/**
 * Normalizuje pojedynczy wpis mapy wiązań do kształtu tagowanego (legacy goły string =
 * `record_field` ze slugiem; pusty string/`null`/`undefined` = brak wiązania). Frontendowe
 * lustro `normalizeParamBinding` z `apps/api/lib/dictionaries/param-binding.ts` — bez
 * rzucania błędów (uszkodzony kształt woła się do serwera, ten odpowiada stabilnym kodem).
 */
export function normalizeDictionaryParamBinding(
  raw: DictionaryParamBindingValue | null | undefined,
): DictionaryParamBinding | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    return raw.length > 0 ? { kind: 'record_field', fieldSlug: raw } : null;
  }
  return raw;
}

/** Wiązanie bez `parent_field` — kształt kontekstów, w których pole nie ma rekordu-rodzica. */
export type DictionaryParamBindingWithoutParent = Exclude<
  DictionaryParamBinding,
  { kind: 'parent_field' }
>;

/**
 * Zawęża mapę wiązań do kontekstu BEZ rekordu-rodzica (np. pola encji szablonu modułu, których
 * współdzielony typ nie zna `parent_field`). `parent_field` w takiej mapie to stan niemożliwy
 * (edytor wiązań go nie emituje) — rzuca jawny błąd zamiast po cichu go odrzucać.
 */
export function assertNoParentFieldBindings(
  bindings: DictionaryParamBindingsMap | null,
): Record<string, string | DictionaryParamBindingWithoutParent> | null {
  if (bindings === null) return null;
  const result: Record<string, string | DictionaryParamBindingWithoutParent> = {};
  for (const [paramName, binding] of Object.entries(bindings)) {
    if (typeof binding !== 'string' && binding.kind === 'parent_field') {
      throw new Error(
        `Wiązanie "parent_field" parametru "${paramName}" jest dozwolone wyłącznie w polach tabel dodatkowych`,
      );
    }
    result[paramName] = binding;
  }
  return result;
}

export interface DictionaryItem {
  /**
   * UUID wiersza źródłowego — `dictionary_items` dla `origin==='local'`,
   * `dictionary_item_extensions` dla `origin==='extension'`. `null` dla
   * `origin==='global'` (pozycje globalne nie mają lokalnego odpowiednika,
   * są identyfikowane wyłącznie przez `code`).
   */
  id: string | null;
  code: string;
  label: string;
  sortOrder: number | null;
  isActive: boolean;
  color: string | null;
  /**
   * Opcjonalna wartość liczbowa pozycji (kolumna `dictionary_items.value`,
   * `numeric(18,6)` → string dziesiętny w transporcie JSON, zero utraty precyzji).
   * Pierwszy konsument: stawki VAT (`vat_rates`) — silnik przeliczeń pozycji
   * dokumentu czyta stawkę stąd, nie parsuje jej z kodu. `null` dla pozycji
   * globalnych/rozszerzeń/zewnętrznych (nie mają odpowiednika tej kolumny).
   */
  value: string | null;
  /**
   * Pochodzenie POZYCJI — może różnić się od originu słownika-rodzica:
   * w słowniku globalnym (`dictionary.origin==='global'`) pozycje mogą być
   * `origin:'global'` (katalog ITE, readonly), `origin:'extension'`
   * (własna pozycja tenanta dopisana do słownika globalnego, Etap 1b) LUB
   * `origin:'external'` (pozycja z zapytania do rejestru źródeł zewnętrznych, Etap 10).
   */
  origin: DictionaryOrigin;
  /** Tenantowe nadpisanie ukrywające pozycję globalną (zawsze false dla origin='local'). */
  isHidden: boolean;
  /** Czy pozycja powinna być proponowana w nowych formularzach (isActive && !isHidden). */
  selectable: boolean;
}

export interface DictionaryDetail {
  code: string;
  name: string;
  description: string | null;
  displayMode: 'label' | 'code' | 'code_label';
  origin: DictionaryOrigin;
  status: DictionaryStatus;
  isSystem: boolean;
  /** true dla słowników globalnych — meta i pozycje nieedytowalne bezpośrednio. */
  readonly: boolean;
  /** UUID lokalnego wiersza `dictionaries` — obecny tylko dla origin='local'. */
  localId: string | null;
  /** UUID wiersza `global_dictionaries` (ADM) — obecny tylko dla origin='global'. */
  globalId: string | null;
  itemCount: number;
  /**
   * 'manual' — pozycje wpisane ręcznie; 'external' — pozycje z zapytania do rejestru
   * źródeł (Etap 10, WYŁĄCZNIE dla origin='global'). Backend ZAWSZE wysyła to pole
   * (patrz `MergedDictionarySummary` w `apps/api/lib/dictionaries/merge.ts`) —
   * OPCJONALNE tu tylko żeby nie łamać tsc w istniejących mockach testowych
   * skonstruowanych przed Etapem 10.5 (np. `purchaseInvoiceCoreRenderers.test.tsx`),
   * które nie ustawiają tego pola. Nowy kod NIE powinien polegać na `undefined` —
   * traktuj brak jak `'manual'` (fallback bezpieczny, bo to jedyna wartość sprzed Etapu 10).
   */
  sourceKind?: DictionarySourceKind;
  /** Klucz rejestru źródeł zewnętrznych — non-null WYŁĄCZNIE dla sourceKind='external'. */
  sourceKey?: string | null;
  /** Parametry zadeklarowane przez źródło — `[]` dla sourceKind='manual'. */
  params?: DictionaryParam[];
  /**
   * Pochodzenie LOKALNEGO słownika tenanta (Faza 4, ADR-0069): 'manual' — utworzony ręcznie;
   * 'erp' — zaimportowany syncem agenta ERP (Symfonia) — pozycje read-only w UI tenanta
   * (backend blokuje mutacje 409 `ERP_SOURCE_PROTECTED`). Opcjonalne dla zgodności tsc ze
   * starszymi mockami — brak traktuj jak 'manual'.
   */
  source?: 'manual' | 'erp';
  /**
   * Ustawione WYŁĄCZNIE gdy backend nie mógł pobrać pozycji słownika ZEWNĘTRZNEGO z powodu
   * brakującego WYMAGANEGO parametru bez jeszcze skonfigurowanego wiązania (mirror
   * `MergedDictionaryDetail.itemsUnavailableReason` w `apps/api/lib/dictionaries/merge.ts`) —
   * `GET /api/dictionaries/:code` i tak zwraca 200 z KOMPLETNYMI metadanymi (`params`
   * włącznie), tylko `items` jest pustą tablicą. `DictionariesTab` pokazuje w tym przypadku
   * informacyjny hint zamiast zwykłego "słownik pusty".
   */
  itemsUnavailableReason?: 'PARAMS_REQUIRED';
  /** Nazwa BRAKUJĄCEGO parametru — obecna tylko razem z `itemsUnavailableReason`. */
  itemsUnavailableParamName?: string;
  items: DictionaryItem[];
}

/** Wpis w liście `GET /dictionaries` (bez pozycji) — patrz `MergedDictionarySummary`. */
export interface DictionarySummary {
  code: string;
  name: string;
  description: string | null;
  displayMode: 'label' | 'code' | 'code_label';
  origin: DictionaryOrigin;
  status: DictionaryStatus;
  isSystem: boolean;
  readonly: boolean;
  localId: string | null;
  globalId: string | null;
  itemCount: number;
  /** Patrz `DictionaryDetail.sourceKind` — identyczna semantyka i sam powód opcjonalności. */
  sourceKind?: DictionarySourceKind;
  sourceKey?: string | null;
  params?: DictionaryParam[];
  /** Patrz `DictionaryDetail.source` — 'erp' = słownik zsynchronizowany z ERP (Symfonia). */
  source?: 'manual' | 'erp';
}

interface GetDictionaryResponse {
  dictionary: DictionaryDetail;
}

interface ListDictionariesResponse {
  dictionaries: DictionarySummary[];
}

/**
 * Kod błędu materializacji słownika zewnętrznego (Etap 10.3, ADR-0066 §11) — mirror kodów
 * `error` zwracanych przez `GET /api/dictionaries/:code` i `POST /api/dictionaries/:code/options`:
 *  - `EXTERNAL_PARAM_MISSING` (400) — wymagany parametr `record_field`/`context.*` nieobecny
 *    (body niesie `paramName`).
 *  - `EXTERNAL_PARAM_INVALID` (400) — `recordParams` (options endpoint) zawiera klucz spoza
 *    zadeklarowanych parametrów źródła albo wartość niekoercowalną (body niesie `paramName`).
 *  - `EXTERNAL_SOURCE_UNAVAILABLE` (503) — awaria zapytania do bazy tenanta przy `fetchItems`.
 * Fallback `| string` — inne kody (np. `GLOBAL_DICTIONARIES_UNAVAILABLE`, `NOT_FOUND`) nadal
 * przechodzą przez ten sam kanał, konsument dostaje surowy `code` bez utraty typowania.
 */
export type DictionaryErrorCode =
  | 'EXTERNAL_PARAM_MISSING'
  | 'EXTERNAL_PARAM_INVALID'
  | 'EXTERNAL_SOURCE_UNAVAILABLE'
  | string;

/** Błąd sparsowany z ciała odpowiedzi axios (`{ error, paramName? }`) — patrz `parseDictionaryError`. */
export interface DictionaryQueryError {
  code: DictionaryErrorCode;
  paramName?: string;
}

/**
 * Wyciąga `{ error, paramName }` z ciała odpowiedzi axios — `null` gdy błąd nie jest
 * axios error z JSON body (np. network error) albo body nie niesie `error` jako string.
 * Analogiczne narrowing do `extractErrorCode` w `lib/errors.ts`, bez `any`.
 */
function parseDictionaryError(err: unknown): DictionaryQueryError | null {
  if (!isAxiosError(err)) return null;
  const data: unknown = err.response?.data;
  if (!data || typeof data !== 'object' || !('error' in data)) return null;
  const code = (data as { error?: unknown }).error;
  if (typeof code !== 'string') return null;
  const paramNameRaw = (data as { paramName?: unknown }).paramName;
  const paramName = typeof paramNameRaw === 'string' ? paramNameRaw : undefined;
  return paramName !== undefined ? { code, paramName } : { code };
}

/**
 * Mapuje błąd materializacji słownika zewnętrznego (Etap 10.5, ADR-0066 §11) na
 * czytelny komunikat i18n — `null` gdy brak błędu, albo kod nie jest jednym z
 * trzech znanych trybów awarii (backend może zwrócić też inne kody, np.
 * `NOT_FOUND`/`GLOBAL_DICTIONARIES_UNAVAILABLE` — te celowo zostają
 * niezmapowane, dropdown po prostu pozostaje pusty, bez mylącego komunikatu).
 *
 * Ekstrakcja z trzech rendererów, które miały identyczną kopię tej funkcji
 * (review finding, pre-commit Etapu 10.5): `SelectFieldType`, `ExtFieldValueInput`,
 * `AdditionalTableTab`.
 */
export function mapDictionaryQueryError(
  error: DictionaryQueryError | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!error) return null;
  switch (error.code) {
    case 'EXTERNAL_PARAM_MISSING':
      return t('tenant.dictionaries.error.paramMissing', { paramName: error.paramName ?? '' });
    case 'EXTERNAL_PARAM_INVALID':
      return t('tenant.dictionaries.error.paramInvalid', { paramName: error.paramName ?? '' });
    case 'EXTERNAL_SOURCE_UNAVAILABLE':
      return t('tenant.dictionaries.error.sourceUnavailable');
    default:
      return null;
  }
}

export interface UseDictionaryResult {
  dictionary: DictionaryDetail | null;
  /** Wszystkie items (aktywne + nieaktywne, ukryte + widoczne). */
  items: DictionaryItem[];
  /** Tylko wybieralne items (isActive && !isHidden) — uzywaj w selectach formularzy. */
  activeItems: DictionaryItem[];
  /** Mapa code→item dla szybkiego lookupu (zawiera tez nieaktywne/ukryte). */
  itemsByCode: Map<string, DictionaryItem>;
  isLoading: boolean;
  isError: boolean;
  /**
   * Błąd sparsowany z odpowiedzi (Etap 10.5) — `null` gdy `isError=false` albo błąd nie
   * niesie rozpoznawalnego `{ error }` w body (np. network error, 401/500 generyczny).
   * Renderer sprawdza `error?.code` żeby pokazać treściwy komunikat (np.
   * `tenant.dictionaries.error.paramMissing` z `{{paramName: error.paramName}}`) zamiast
   * generycznego "błąd ładowania".
   */
  error: DictionaryQueryError | null;
}

export const DICTIONARIES_LIST_KEY = ['dictionaries'] as const;
export const dictionaryDetailKey = (code: string) => ['dictionaries', code] as const;

/**
 * Formatuje etykietę pozycji słownika do pokazania w opcji <select>/<option>
 * (lub chipie MultiSelect) zgodnie z `displayMode` słownika-rodzica:
 *  - 'label'      → sam label ("Siedziba")
 *  - 'code'       → sam kod ("hq")
 *  - 'code_label' → `${code} — ${label}` ("hq — Siedziba")
 *
 * Wspólne dla wszystkich rendererów pól select/multiselect z dictionaryCode
 * (`ExtFieldValueInput`, `AdditionalTableTab`) — unika duplikacji logiki
 * displayMode w dwóch miejscach.
 */
export function formatDictionaryOptionLabel(
  item: Pick<DictionaryItem, 'code' | 'label'>,
  displayMode: DictionaryDetail['displayMode'],
): string {
  switch (displayMode) {
    case 'code':
      return item.code;
    case 'code_label':
      return `${item.code} — ${item.label}`;
    case 'label':
    default:
      return item.label;
  }
}

/**
 * True iff a field's `dictionaryParamBindings` is non-null and carries at least
 * one entry (legacy string OR tagged binding) — the gate every param-bound-dictionary
 * renderer uses to decide whether to fetch options via `useDictionaryOptions`
 * (per-field bindings) instead of the plain `useDictionary` (static) path.
 *
 * Extracted from three renderers that each computed this inline (Etap 10.3,
 * ADR-0066 §11): `ExtFieldValueInput`, `AdditionalTableTab`, `SelectFieldType`.
 */
export function hasParamBindings(bindings: DictionaryParamBindingsMap | null | undefined): boolean {
  return !!bindings && Object.keys(bindings).length > 0;
}

/**
 * Pobiera szczegóły słownika po kodzie (`GET /api/dictionaries/:code`) — to samo zapytanie, które
 * woła `useDictionary`, wystawione osobno dla konsumentów ładujących kilka słowników naraz
 * (`useQueries`, np. opcje filtra w wierszu filtrów list), żeby współdzieliły klucz cache
 * (`dictionaryDetailKey`) i nie dublowały żądania.
 */
export async function fetchDictionaryDetail(code: string): Promise<DictionaryDetail> {
  const res = await api.get<GetDictionaryResponse>(`/dictionaries/${encodeURIComponent(code)}`);
  return res.data.dictionary;
}

/** Czas świeżości szczegółu słownika — wspólny dla `useDictionary` i `useQueries` po `fetchDictionaryDetail`. */
export const DICTIONARY_DETAIL_STALE_TIME_MS = 2 * 60 * 1000;

export function useDictionary(code: string): UseDictionaryResult {
  const query = useQuery({
    queryKey: dictionaryDetailKey(code),
    queryFn: () => fetchDictionaryDetail(code),
    staleTime: DICTIONARY_DETAIL_STALE_TIME_MS,
    enabled: !!code,
  });

  const dictionary = query.data ?? null;
  const items = dictionary?.items ?? [];
  const activeItems = items.filter((i) => i.selectable);
  const itemsByCode = new Map(items.map((i) => [i.code, i] as const));

  return {
    dictionary,
    items,
    activeItems,
    itemsByCode,
    isLoading: query.isLoading,
    isError: query.isError,
    error: parseDictionaryError(query.error),
  };
}

/**
 * Zapisana treść zapytania LOKALNEGO słownika SQL (`GET /api/dictionaries/:code/source`).
 * Ujawniana WYŁĄCZNIE posiadaczowi `crm_core.dictionaries.manage_sql` — bez uprawnienia treść
 * zapytania nie wycieka.
 */
export interface DictionarySource {
  sourceSql: string;
  /** Limit czasu wykonania SQL (ms). Dziś nieużywany przez formularz edycji (brak pola limitu). */
  statementTimeoutMs: number;
  declaredParams: DictionaryParam[];
}

export interface UseDictionarySourceResult {
  source: DictionarySource | null;
  isLoading: boolean;
  isError: boolean;
  /** Status HTTP błędu (np. 404 = słownik usunięty lub już nie-SQL); `null` bez błędu lub bez odpowiedzi (sieć). */
  errorStatus: number | null;
}

/** Klucz poza prefiksem `['dictionaries']` — invalidacje listy i szczegółów go nie dotykają. */
export const dictionarySourceKey = (code: string) => ['dictionary-source', code] as const;

/**
 * Wczytuje zapisaną treść zapytania słownika SQL do formularza edycji. Osobna od `useDictionary`:
 * szczegóły WYKONUJĄ zapytanie i przy jego błędzie (503) nie niosą SQL-a — a właśnie zepsute
 * zapytanie chce się poprawić. `gcTime: 0` + `staleTime: 0` — wynik nie żyje po zamknięciu edycji,
 * więc każde otwarcie czyta aktualny tekst od zera (bez starego SQL-a ani starego błędu z cache'u,
 * które okno edycji pokazałoby przed odświeżeniem). `retry: false` — brak uprawnienia lub słownika
 * to nie błąd przejściowy, komunikat ma być od razu.
 * Pusty `code` wyłącza zapytanie (tworzenie, edycja słownika ręcznego). Wynik nie przeżywa
 * zamknięcia okna (`gcTime: 0`), więc invalidacja po zapisie nie jest potrzebna — dlatego klucz
 * leży poza prefiksem `['dictionaries']` i zapis słownika nie wywołuje zbędnego refetchu.
 */
export function useDictionarySource(code: string): UseDictionarySourceResult {
  const query = useQuery({
    queryKey: dictionarySourceKey(code),
    queryFn: async (): Promise<DictionarySource> => {
      const res = await api.get<{ source: DictionarySource }>(
        `/dictionaries/${encodeURIComponent(code)}/source`,
      );
      return res.data.source;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: !!code,
  });

  return {
    source: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    errorStatus: isAxiosError(query.error) ? (query.error.response?.status ?? null) : null,
  };
}

/**
 * useDictionaryOptions — Etap 10.3 (ADR-0066 §11), rozszerzone Fazą 2b (ADR-0068) do modelu
 * wiązań dwuetapowego. Pobiera pozycje słownika PRZEZ `POST /api/dictionaries/:code/options`
 * zamiast `GET /api/dictionaries/:code` (`useDictionary`) — dla pól z niepustym
 * `dictionaryParamBindings`, których opcje zależą od wiązań per-pole (pole rekordu / firma /
 * user / stała), nie tylko od kontekstu firmy/usera.
 *
 * Ścieżka NOWA (Faza 2b): renderer podaje `bindings` (RAW `dictionaryParamBindings` pola —
 * tagowane lub legacy stringi) + `recordValues` (wartości pól TEGO rekordu, kluczowane SLUGIEM
 * pola). Rozwiązywanie wartości per parametr robi SERWER (w tym `context.company`/`context.user`
 * z nagłówka/JWT — klient ich nie wysyła i nie może podrobić).
 *
 * Ścieżka LEGACY (Etap 10.3): `recordParams` — mapa nazwa parametru → wartość JUŻ ROZWIĄZANA
 * po stronie klienta. Utrzymywana dla zgodności wstecznej; nowi konsumenci używają
 * `bindings`+`recordValues`. Gdy podane są `bindings` (niepuste), wygrywają nad `recordParams`.
 *
 * `enabled` — renderer woła to PRZY OTWARCIU dropdownu (nie na mount formularza), żeby nie
 * płacić za fetch, dopóki user nie zamierza wybrać wartości.
 *
 * ZERO cache między otwarciami z różnym wejściem: `staleTime: 0` + `queryKey` zawierający
 * zserializowane body gwarantują, że (a) zmiana wartości sąsiada między otwarciami zawsze
 * skutkuje ŚWIEŻYM zapytaniem sieciowym (inny klucz — nigdy trafienie w cache poprzedniego
 * zestawu), (b) ponowne otwarcie z NIEZMIENIONYM wejściem też odświeża (ten sam klucz, ale
 * `staleTime=0` — dane natychmiast "stale", więc ponowne `enabled: true` wyzwala refetch).
 *
 * Zwraca DOKŁADNIE ten sam kształt co `useDictionary` (items/activeItems/itemsByCode/
 * isLoading/isError) — renderer może przełączać się między obiema ścieżkami bez zmiany
 * konsumowanego kontraktu.
 */
export interface UseDictionaryOptionsParams {
  /**
   * RAW `dictionaryParamBindings` pola (tagowane lub legacy stringi) — wysyłane WPROST do
   * `POST .../options` (pole `bindings` body). Niepuste → ścieżka nowa (wygrywa nad
   * `recordParams`).
   */
  bindings?: DictionaryParamBindingsMap | null;
  /** Wartości pól TEGO rekordu kluczowane SLUGIEM pola — źródło dla wiązań `record_field`. */
  recordValues?: Record<string, unknown>;
  /**
   * Wartości pól rekordu-RODZICA (kartoteki właściciela tabeli dodatkowej) kluczowane slugiem
   * pola rodzica — źródło dla wiązań `parent_field`. Tylko skalary/`null` (wołający zawęża je do
   * slugów używanych przez wiązania pola i koercjuje, patrz `lib/additional-tables/
   * parent-field-values.ts`). `undefined` = pole `parentValues` NIE trafia do body.
   */
  parentValues?: Record<string, string | number | boolean | null>;
  /** Klucz wyboru pola rodzica w wiązaniu `parent_field` (`'default'` albo klasa dokumentu). */
  parentKey?: ParentFieldBindingKey;
  /**
   * LEGACY (Etap 10.3): mapa nazwa-parametru-źródła → wartość rozwiązana po stronie klienta.
   * Używane tylko gdy `bindings` puste/nieobecne.
   */
  recordParams?: Record<string, unknown>;
  /** Odpal fetch WYŁĄCZNIE gdy true (np. dropdown otwarty) — nigdy na mount. */
  enabled: boolean;
}

export function useDictionaryOptions(
  code: string,
  {
    bindings,
    recordValues,
    parentValues,
    parentKey,
    recordParams,
    enabled,
  }: UseDictionaryOptionsParams,
): UseDictionaryResult {
  const useBindingsPath = hasParamBindings(bindings);
  // `parentValues`/`parentKey` wchodzą do body (a więc i do `queryKey` niżej) tylko na ścieżce
  // wiązań i tylko gdy podane — body pól bez `parent_field` zostaje bajt w bajt jak dotąd.
  const body: Record<string, unknown> = useBindingsPath
    ? {
        bindings,
        recordValues: recordValues ?? {},
        ...(parentValues !== undefined ? { parentValues } : {}),
        ...(parentKey !== undefined ? { parentKey } : {}),
      }
    : { recordParams: recordParams ?? {} };

  // Serializacja do queryKey — inne body MUSI dać inny klucz, inaczej otwarcie dropdownu
  // po zmianie wartości sąsiada trafiłoby w cache poprzedniego fetcha.
  const bodyKey = JSON.stringify(body);

  const query = useQuery({
    queryKey: [...dictionaryDetailKey(code), 'options', bodyKey],
    queryFn: async () => {
      const res = await api.post<GetDictionaryResponse>(`/dictionaries/${code}/options`, body);
      return res.data.dictionary;
    },
    staleTime: 0,
    enabled: enabled && !!code,
  });

  const dictionary = query.data ?? null;
  const items = dictionary?.items ?? [];
  const activeItems = items.filter((i) => i.selectable);
  const itemsByCode = new Map(items.map((i) => [i.code, i] as const));

  return {
    dictionary,
    items,
    activeItems,
    itemsByCode,
    isLoading: query.isLoading,
    isError: query.isError,
    error: parseDictionaryError(query.error),
  };
}

// ============================================================================
// Klient zarządzania słownikami tenanta (Settings → Słowniki, Etap 3b)
// ============================================================================

export function useDictionariesList() {
  return useQuery({
    queryKey: DICTIONARIES_LIST_KEY,
    queryFn: async () => {
      const res = await api.get<ListDictionariesResponse>('/dictionaries');
      return res.data.dictionaries;
    },
  });
}

/**
 * Pola źródła LOKALNEGO słownika SQL w ciele żądania create/update. UWAGA na asymetrię
 * kontraktu: w ŻĄDANIU `sourceKind` przyjmuje wartość BAZY `'manual' | 'sql'` (nie wire
 * `'external'`) — backend odbija ją z powrotem na drut jako `'manual' | 'external'`. Pominięcie
 * pól źródła = brak zmiany (w update) / słownik ręczny (w create). Wysyłka `sourceKind:'sql'`
 * wymaga po stronie backendu uprawnienia `crm_core.dictionaries.manage_sql`.
 */
export interface DictionarySqlSourceInput {
  sourceKind?: 'manual' | 'sql';
  sourceSql?: string | null;
  declaredParams?: DictionaryParam[];
  statementTimeoutMs?: number;
}

export interface CreateDictionaryInput extends DictionarySqlSourceInput {
  code: string;
  name: string;
  description?: string;
  displayMode?: 'label' | 'code' | 'code_label';
}

export interface UpdateDictionaryInput extends DictionarySqlSourceInput {
  name?: string;
  description?: string | null;
  displayMode?: 'label' | 'code' | 'code_label';
}

export interface CreateDictionaryItemInput {
  code: string;
  label: string;
  sortOrder?: number;
  color?: string;
  /** Wartość liczbowa pozycji jako string dziesiętny (np. stawka VAT); `null` czyści. */
  value?: string | null;
}

export interface UpdateDictionaryItemInput {
  label?: string;
  sortOrder?: number;
  color?: string | null;
  isActive?: boolean;
  /** Wartość liczbowa pozycji jako string dziesiętny; `null` czyści, pominięcie nie tyka. */
  value?: string | null;
}

/**
 * Mutacje zarządzania katalogiem słowników tenanta — wspólny invalidation dla
 * listy scalonej + szczegółu (klucz per-code). Wszystkie mutacje wymagają
 * `crm_core.dictionaries.manage` po stronie backendu (403 PERMISSION_DENIED
 * gdy brak) — gate przycisków po stronie UI robi caller (`DictionariesTab`)
 * przez `me.permissions`, to tylko warstwa transportu.
 */
export function useDictionaryMutations() {
  const queryClient = useQueryClient();

  function invalidateList() {
    return queryClient.invalidateQueries({ queryKey: DICTIONARIES_LIST_KEY });
  }
  function invalidateDetail(code: string) {
    return queryClient.invalidateQueries({ queryKey: dictionaryDetailKey(code) });
  }

  const createDictionary = useMutation({
    mutationFn: async (values: CreateDictionaryInput) => {
      const res = await api.post<{ dictionary: DictionarySummary }>('/dictionaries', values);
      return res.data.dictionary;
    },
    onSuccess: () => invalidateList(),
  });

  const updateDictionary = useMutation({
    mutationFn: async ({ code, values }: { code: string; values: UpdateDictionaryInput }) => {
      const res = await api.put<{ dictionary: DictionarySummary }>(`/dictionaries/${code}`, values);
      return res.data.dictionary;
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  const deleteDictionary = useMutation({
    mutationFn: async (code: string) => {
      await api.delete(`/dictionaries/${code}`);
    },
    onSuccess: (_data, code) => {
      invalidateList();
      invalidateDetail(code);
    },
  });

  const restoreDictionary = useMutation({
    mutationFn: async (code: string) => {
      await api.post(`/dictionaries/${code}/restore`);
    },
    onSuccess: (_data, code) => {
      invalidateList();
      invalidateDetail(code);
    },
  });

  const copyGlobalToLocal = useMutation({
    mutationFn: async (code: string) => {
      const res = await api.post<{ dictionary: DictionarySummary }>(`/dictionaries/${code}/copy`);
      return res.data.dictionary;
    },
    onSuccess: (data, code) => {
      // Odpowiedź POST .../copy niesie już poprawny `itemCount` policzony przez
      // backend — wpisujemy go do cache'u listy NATYCHMIAST zamiast czekać na
      // async refetch spod invalidateList() (ten sam kod, tylko origin zmienia
      // się global→local — patrz `DictionarySummary.code` niezmienne przy kopii).
      // Bez tego lista przez chwilę (do zakończenia refetcha w tle) pokazywała
      // stary wiersz global z itemCount sprzed skopiowania pozycji.
      queryClient.setQueryData<DictionarySummary[]>(DICTIONARIES_LIST_KEY, (old) => {
        if (!old) return old;
        const idx = old.findIndex((d) => d.code === code);
        if (idx === -1) return [...old, data];
        const next = [...old];
        next[idx] = data;
        return next;
      });
      invalidateList();
      invalidateDetail(code);
    },
  });

  const createItem = useMutation({
    mutationFn: async ({ code, values }: { code: string; values: CreateDictionaryItemInput }) => {
      const res = await api.post<{ item: DictionaryItem }>(`/dictionaries/${code}/items`, values);
      return res.data.item;
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  const updateItem = useMutation({
    mutationFn: async ({
      code,
      itemId,
      values,
    }: {
      code: string;
      itemId: string;
      values: UpdateDictionaryItemInput;
    }) => {
      const res = await api.put<{ item: DictionaryItem }>(
        `/dictionaries/${code}/items/${itemId}`,
        values,
      );
      return res.data.item;
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  const deleteItem = useMutation({
    mutationFn: async ({ code, itemId }: { code: string; itemId: string }) => {
      await api.delete(`/dictionaries/${code}/items/${itemId}`);
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  /**
   * Edycja własnej pozycji dopisanej do słownika GLOBALNEGO (Etap 1b —
   * `dictionary_item_extensions`). Osobny endpoint od `updateItem` — pozycji
   * `origin==='extension'` NIE MA w tabeli `dictionary_items`, więc
   * `.../items/:itemId` zwróciłoby 404.
   */
  const updateExtension = useMutation({
    mutationFn: async ({
      code,
      itemId,
      values,
    }: {
      code: string;
      itemId: string;
      values: UpdateDictionaryItemInput;
    }) => {
      const res = await api.put<{ item: DictionaryItem }>(
        `/dictionaries/${code}/extensions/${itemId}`,
        values,
      );
      return res.data.item;
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  /** Usunięcie (soft-delete) własnej pozycji dopisanej do słownika GLOBALNEGO. */
  const deleteExtension = useMutation({
    mutationFn: async ({ code, itemId }: { code: string; itemId: string }) => {
      await api.delete(`/dictionaries/${code}/extensions/${itemId}`);
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  /** Ukrywa/odkrywa pojedynczą pozycję słownika GLOBALNEGO dla tego tenanta. */
  const setItemHidden = useMutation({
    mutationFn: async ({
      code,
      itemCode,
      hidden,
    }: {
      code: string;
      itemCode: string;
      hidden: boolean;
    }) => {
      if (hidden) {
        await api.post(`/dictionaries/${code}/hidden-items`, { itemCode });
      } else {
        await api.delete(`/dictionaries/${code}/hidden-items`, { data: { itemCode } });
      }
    },
    onSuccess: (_data, variables) => {
      invalidateList();
      invalidateDetail(variables.code);
    },
  });

  return {
    createDictionary,
    updateDictionary,
    deleteDictionary,
    restoreDictionary,
    copyGlobalToLocal,
    createItem,
    updateItem,
    deleteItem,
    updateExtension,
    deleteExtension,
    setItemHidden,
  };
}

// ============================================================================
// Test SQL — próbne wykonanie zapytania lokalnego słownika SQL (Ustawienia → Słowniki)
// ============================================================================

/**
 * Ciało `POST /api/dictionaries/test-sql` — próbne wykonanie SQL na bazie WŁASNEGO tenanta
 * (bez wyboru tenanta, w przeciwieństwie do wariantu admina). `paramValues` to przykładowe
 * wartości parametrów wpisane wprost przez autora (wiązania `record_field`/`context.*` to koncept
 * runtime, w Ustawieniach nie ma jeszcze rekordu — testujemy wartościami).
 */
export interface TestDictionarySqlInput {
  sourceSql: string;
  declaredParams?: DictionaryParam[];
  paramValues?: Record<string, string | number | boolean | null>;
}

/** Wynik testu SQL — grid pierwszych wierszy + licznik + flaga obcięcia. */
export interface TestDictionarySqlResult {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  truncated: boolean;
}

/**
 * Mutacja „Testuj zapytanie SQL" słownika lokalnego — reużywa produkcyjnego egzekutora
 * (`executeDictionarySql`) na bazie tenanta pod rolą read-only. Błędy walidacji wracają jako
 * 400 z kodami `SQL_*` (mapowane na komunikaty w modalu), błąd wykonania jako 422
 * `SQL_EXECUTION_FAILED` z surowym komunikatem Postgresa. Brama backendu:
 * `crm_core.dictionaries.manage_sql`.
 */
export function useTestDictionarySql() {
  return useMutation({
    mutationFn: async (input: TestDictionarySqlInput) => {
      const res = await api.post<TestDictionarySqlResult>('/dictionaries/test-sql', input);
      return res.data;
    },
  });
}
